import React, { useState, useRef } from 'react';
import { Upload, Play, Download, X, Settings, Image as ImageIcon } from 'lucide-react';
import './ImageToVideo.css';

const TRANSITIONS = [
  { value: 'fade', label: 'Fade', description: 'Smooth crossfade' },
  { value: 'zoom', label: 'Zoom In', description: 'Ken Burns effect' },
  { value: 'zoomOut', label: 'Zoom Out', description: 'Reverse zoom' },
  { value: 'slide', label: 'Slide Right', description: 'Pan movement' },
  { value: 'slideLeft', label: 'Slide Left', description: 'Pan left' },
  { value: 'rotate', label: 'Rotate', description: 'Gentle rotation' },
  { value: 'blur', label: 'Blur Fade', description: 'Blur transition' },
  { value: 'pixelate', label: 'Pixelate', description: 'Pixel dissolve' },
  { value: 'wipe', label: 'Wipe', description: 'Directional wipe' },
  { value: 'circle', label: 'Circle Reveal', description: 'Circular reveal' },
  { value: 'crossZoom', label: 'Cross Zoom', description: 'Zoom crossfade' },
  { value: 'glitch', label: 'Glitch', description: 'Digital glitch effect' }
];

function ImageToVideo() {
  const [images, setImages] = useState([]);
  const [duration, setDuration] = useState(3);
  const [transition, setTransition] = useState('fade');
  const [transitionDuration, setTransitionDuration] = useState(0.5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = [...e.dataTransfer.files].filter(file => file.type.startsWith('image/'));
    handleFiles(files);
  };

  const handleFileInput = (e) => {
    const files = [...e.target.files];
    handleFiles(files);
  };

  const handleFiles = (files) => {
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          url: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const moveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < images.length) {
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      setImages(newImages);
    }
  };

  const applyTransition = (ctx, canvas, frameCount, framesPerImage, transitionFrames, type) => {
    const progress = frameCount / framesPerImage;
    const transitionProgress = Math.min(frameCount / transitionFrames, 1);

    switch(type) {
      case 'fade':
        if (frameCount < transitionFrames) {
          return { opacity: transitionProgress };
        } else if (frameCount > framesPerImage - transitionFrames) {
          return { opacity: (framesPerImage - frameCount) / transitionFrames };
        }
        return { opacity: 1 };

      case 'zoom':
        const scale = 1 + progress * 0.15;
        return { scale, centerTransform: true };

      case 'zoomOut':
        const scaleOut = 1.15 - progress * 0.15;
        return { scale: scaleOut, centerTransform: true };

      case 'slide':
        const slideOffset = progress * canvas.width * 0.1;
        return { offsetX: -slideOffset };

      case 'slideLeft':
        const slideLeftOffset = progress * canvas.width * 0.1;
        return { offsetX: slideLeftOffset };

      case 'rotate':
        const rotation = progress * 0.05;
        return { rotation, centerTransform: true };

      case 'blur':
        if (frameCount < transitionFrames) {
          ctx.filter = `blur(${(1 - transitionProgress) * 10}px)`;
          return { opacity: transitionProgress };
        } else if (frameCount > framesPerImage - transitionFrames) {
          const fadeProgress = (framesPerImage - frameCount) / transitionFrames;
          ctx.filter = `blur(${(1 - fadeProgress) * 10}px)`;
          return { opacity: fadeProgress };
        }
        ctx.filter = 'none';
        return { opacity: 1 };

      case 'pixelate':
        if (frameCount < transitionFrames) {
          const pixelSize = Math.max(1, Math.floor((1 - transitionProgress) * 20));
          return { pixelate: pixelSize, opacity: transitionProgress };
        } else if (frameCount > framesPerImage - transitionFrames) {
          const fadeProgress = (framesPerImage - frameCount) / transitionFrames;
          const pixelSize = Math.max(1, Math.floor((1 - fadeProgress) * 20));
          return { pixelate: pixelSize, opacity: fadeProgress };
        }
        return { opacity: 1 };

      case 'wipe':
        if (frameCount < transitionFrames) {
          return { wipe: transitionProgress };
        }
        return { opacity: 1 };

      case 'circle':
        if (frameCount < transitionFrames) {
          return { circleReveal: transitionProgress };
        }
        return { opacity: 1 };

      case 'crossZoom':
        const crossScale = 1 + Math.sin(progress * Math.PI) * 0.1;
        let crossOpacity = 1;
        if (frameCount < transitionFrames) {
          crossOpacity = transitionProgress;
        } else if (frameCount > framesPerImage - transitionFrames) {
          crossOpacity = (framesPerImage - frameCount) / transitionFrames;
        }
        return { scale: crossScale, centerTransform: true, opacity: crossOpacity };

      case 'glitch':
        if (frameCount < transitionFrames || frameCount > framesPerImage - transitionFrames) {
          const glitchIntensity = frameCount < transitionFrames
            ? (1 - transitionProgress)
            : (1 - (framesPerImage - frameCount) / transitionFrames);
          return {
            glitch: glitchIntensity,
            opacity: frameCount < transitionFrames ? transitionProgress : (framesPerImage - frameCount) / transitionFrames
          };
        }
        return { opacity: 1 };

      default:
        return { opacity: 1 };
    }
  };

  const drawImageWithEffects = (ctx, img, canvas, drawX, drawY, drawWidth, drawHeight, effects) => {
    ctx.save();

    // Apply opacity
    if (effects.opacity !== undefined) {
      ctx.globalAlpha = effects.opacity;
    }

    // Apply transformations
    if (effects.centerTransform) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      if (effects.scale) {
        ctx.scale(effects.scale, effects.scale);
      }
      if (effects.rotation) {
        ctx.rotate(effects.rotation);
      }
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }

    // Apply offset
    if (effects.offsetX) {
      drawX += effects.offsetX;
    }
    if (effects.offsetY) {
      drawY += effects.offsetY;
    }

    // Handle special effects
    if (effects.pixelate && effects.pixelate > 1) {
      const pixelSize = effects.pixelate;
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      const w = Math.floor(drawWidth / pixelSize);
      const h = Math.floor(drawHeight / pixelSize);
      tempCanvas.width = w;
      tempCanvas.height = h;
      tempCtx.drawImage(img, 0, 0, w, h);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(tempCanvas, drawX, drawY, drawWidth, drawHeight);
      ctx.imageSmoothingEnabled = true;
    } else if (effects.wipe !== undefined) {
      ctx.beginPath();
      ctx.rect(drawX, drawY, drawWidth * effects.wipe, drawHeight);
      ctx.clip();
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    } else if (effects.circleReveal !== undefined) {
      const maxRadius = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height) / 2;
      const radius = maxRadius * effects.circleReveal;
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, radius, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    } else if (effects.glitch) {
      const slices = 10;
      const sliceHeight = drawHeight / slices;
      for (let i = 0; i < slices; i++) {
        const offset = (Math.random() - 0.5) * 20 * effects.glitch;
        ctx.drawImage(
          img,
          0, (img.height / slices) * i, img.width, img.height / slices,
          drawX + offset, drawY + sliceHeight * i, drawWidth, sliceHeight
        );
      }
    } else {
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }

    ctx.restore();
  };

  const generateVideo = async () => {
    if (images.length === 0) return;

    setIsGenerating(true);
    setProgress(0);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    canvas.width = 1920;
    canvas.height = 1080;

    const stream = canvas.captureStream(30);
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 5000000
    });

    const chunks = [];
    mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
      setIsGenerating(false);
      setProgress(100);
    };

    mediaRecorder.start();

    let currentImageIndex = 0;
    let frameCount = 0;
    const fps = 30;
    const framesPerImage = duration * fps;
    const transitionFrames = fps * transitionDuration;

    const animate = () => {
      if (currentImageIndex >= images.length) {
        mediaRecorder.stop();
        return;
      }

      const progressPercent = ((currentImageIndex * framesPerImage + frameCount) / (images.length * framesPerImage)) * 100;
      setProgress(Math.floor(progressPercent));

      const img = new Image();
      img.src = images[currentImageIndex].url;
      img.onload = () => {
        const drawFrame = () => {
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.filter = 'none';

          const imgAspect = img.width / img.height;
          const canvasAspect = canvas.width / canvas.height;
          let drawWidth, drawHeight, drawX, drawY;

          if (imgAspect > canvasAspect) {
            drawHeight = canvas.height;
            drawWidth = drawHeight * imgAspect;
            drawX = (canvas.width - drawWidth) / 2;
            drawY = 0;
          } else {
            drawWidth = canvas.width;
            drawHeight = drawWidth / imgAspect;
            drawX = 0;
            drawY = (canvas.height - drawHeight) / 2;
          }

          const effects = applyTransition(ctx, canvas, frameCount, framesPerImage, transitionFrames, transition);
          drawImageWithEffects(ctx, img, canvas, drawX, drawY, drawWidth, drawHeight, effects);

          frameCount++;

          if (frameCount < framesPerImage) {
            setTimeout(drawFrame, 1000 / fps);
          } else {
            frameCount = 0;
            currentImageIndex++;
            animate();
          }
        };

        drawFrame();
      };
    };

    animate();
  };

  return (
    <div id="image-to-animation" className="image-to-video-container">
      <div className="itv-content-wrapper">
        {/* Header */}
        <div className="itv-header">
          <h1 className="itv-title">Motion Frame</h1>
          <p className="itv-subtitle">Transform your images into cinematic animations</p>
        </div>

        {/* Upload Zone */}
        <div
          className={`itv-drop-zone ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: 'none' }}
            id="itv-file-input"
          />
          <label htmlFor="itv-file-input" className="itv-upload-label">
            <Upload size={56} className="itv-upload-icon" />
            <h3 className="itv-upload-title">Drop your images here</h3>
            <p className="itv-upload-text">or click to browse • Supports JPG, PNG, GIF</p>
          </label>
        </div>

        {/* Controls */}
        {images.length > 0 && (
          <div className="itv-controls-panel">
            <div className="itv-panel-header">
              <Settings size={28} className="itv-settings-icon" />
              <h2 className="itv-panel-title">Animation Settings</h2>
            </div>

            <div className="itv-controls-grid">
              <div className="itv-control-group">
                <label className="itv-control-label">Duration per image</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="0.5"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="itv-range-input"
                />
                <div className="itv-control-value">{duration}s</div>
              </div>

              <div className="itv-control-group">
                <label className="itv-control-label">Transition duration</label>
                <input
                  type="range"
                  min="0.2"
                  max="2"
                  step="0.1"
                  value={transitionDuration}
                  onChange={(e) => setTransitionDuration(Number(e.target.value))}
                  className="itv-range-input"
                />
                <div className="itv-control-value">{transitionDuration}s</div>
              </div>
            </div>

            <div className="itv-transition-selector">
              <label className="itv-control-label">Transition Effect</label>
              <div className="itv-transition-grid">
                {TRANSITIONS.map((trans) => (
                  <button
                    key={trans.value}
                    onClick={() => setTransition(trans.value)}
                    className={`itv-transition-card ${transition === trans.value ? 'active' : ''}`}
                  >
                    <div className="itv-transition-name">{trans.label}</div>
                    <div className="itv-transition-desc">{trans.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={generateVideo}
              disabled={isGenerating}
              className={`itv-generate-button ${isGenerating ? 'generating' : ''}`}
            >
              <Play size={24} className={isGenerating ? 'pulse' : ''} />
              {isGenerating ? `Generating... ${progress}%` : 'Generate Video'}
            </button>
          </div>
        )}

        {/* Image Gallery */}
        {images.length > 0 && (
          <div className="itv-gallery-panel">
            <div className="itv-gallery-header">
              <ImageIcon size={28} className="itv-gallery-icon" />
              <h2 className="itv-panel-title">Your Images ({images.length})</h2>
            </div>
            <div className="itv-image-grid">
              {images.map((image, index) => (
                <div key={image.id} className="itv-image-card">
                  <img src={image.url} alt={image.name} className="itv-image-preview" />
                  <div className="itv-image-info">
                    <div className="itv-image-frame">Frame {index + 1}</div>
                    <div className="itv-image-name">{image.name}</div>
                  </div>
                  <div className="itv-image-actions">
                    {index > 0 && (
                      <button
                        onClick={() => moveImage(index, 'up')}
                        className="itv-image-action-btn"
                        title="Move up"
                      >
                        ↑
                      </button>
                    )}
                    {index < images.length - 1 && (
                      <button
                        onClick={() => moveImage(index, 'down')}
                        className="itv-image-action-btn"
                        title="Move down"
                      >
                        ↓
                      </button>
                    )}
                  </div>
                  <button onClick={() => removeImage(image.id)} className="itv-remove-button">
                    <X size={20} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video Preview */}
        {videoUrl && (
          <div className="itv-video-panel">
            <h2 className="itv-panel-title">Your Animation</h2>
            <video ref={videoRef} src={videoUrl} controls className="itv-video-preview" />
            <a href={videoUrl} download="animation.webm" className="itv-download-button">
              <Download size={20} />
              Download Video
            </a>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}

export default ImageToVideo;
